import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';

// Import local product images
import BearingImage from '../images/bearing.jpeg';
import OilSealImage from '../images/oil_seal.jpeg';
import BeltImage from '../images/belt.jpeg';
import ToolsImage from '../images/tools.jpeg';

const MotionCard = motion(Card);

const products = [
  {
    title: 'Bearings',
    description: 'High-quality ball bearings, roller bearings, thrust bearings from top manufacturers.',
    image: BearingImage,
  },
  {
    title: 'Oil Seals',
    description: 'Premium oil seals and gaskets for automotive, industrial, and hydraulic applications.',
    image: OilSealImage,
  },
  {
    title: 'Belts',
    description: 'V-belts, timing belts, conveyor belts, and industrial power transmission belts.',
    image: BeltImage,
  },
  {
    title: 'Auto Parts',
    description: 'Comprehensive range of automotive spare parts for all major vehicle brands.',
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop',
  },
  {
    title: 'Tools',
    description: 'Professional hand tools, power tools, and specialized industrial tools.',
    image: ToolsImage,
  },
  {
    title: 'Industrial Equipment',
    description: 'Heavy-duty industrial equipment, machinery parts, and maintenance supplies.',
    image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400&h=300&fit=crop',
  },
];

const Products = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      id="products"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: isDark ? 'background.default' : '#f8f9fa',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '400px',
          bgcolor: isDark ? '#1a1a1a' : '#f5f5f5', 
          zIndex: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: `linear-gradient(to bottom, transparent, ${isDark ? '#121212' : '#f8f9fa'})`,
          }
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 10, color: isDark ? 'white' : 'text.primary' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#e57373',
              fontWeight: 700,
              letterSpacing: 3,
              fontSize: '0.9rem',
              display: 'block',
              mb: 2,
            }}
          >
            OUR CATALOG
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              color: isDark ? 'white' : 'text.primary',
              letterSpacing: '0.05em',
              mb: 3,
              textShadow: isDark ? '0 4px 10px rgba(0,0,0,0.3)' : 'none',
            }}
          >
            PREMIUM <span style={{ color: '#e57373' }}>PRODUCTS</span>
          </Typography>
          <Typography
            sx={{
              color: isDark ? 'rgba(255,255,255,0.8)' : 'text.secondary',
              maxWidth: 700,
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
              lineHeight: 1.8,
              fontFamily: '"Rajdhani", sans-serif',
            }}
          >
            We provide a comprehensive range of auto parts, tools, and industrial equipment to meet all your needs.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {products.map((product, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  y: -12,
                }}
                sx={{
                  height: '100%',
                  bgcolor: isDark ? '#1e1e1e' : 'white',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid',
                  borderColor: isDark ? '#333' : 'rgba(0,0,0,0.05)',
                  boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    borderColor: '#e57373',
                    '& .product-image': {
                      transform: 'scale(1.1)',
                    },
                    '& .product-overlay': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Box sx={{ position: 'relative', height: 240, overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    className="product-image"
                    image={product.image}
                    alt={product.title}
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <Box
                    className="product-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(0,0,0,0.3)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '40%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      left: 20,
                      color: 'white',
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      fontSize: '1.4rem',
                      fontWeight: 700,
                      letterSpacing: 1,
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {product.title}
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                      mb: 2,
                      minHeight: 48, // approximate height for 2 lines
                    }}
                  >
                    {product.description}
                  </Typography>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 3, 
                      bgcolor: '#e57373', 
                      borderRadius: 1,
                      transition: 'width 0.3s ease',
                      '.MuiCard-root:hover &': {
                        width: '100%',
                      }
                    }} 
                  />
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Products;
